import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { ILastContacts, ITotalActive, IUserContactGroup } from '../models/user-group.model';
import {
  IContact,
  IContactActivityNote,
  IContactFieldsMapping,
  IContactGroup,
  IContactItem,
  IMoveContactResult,
} from '../models/contact.model';
import { map, tap } from 'rxjs/operators';
import { IBaseTableResult } from '../models/table.model';
import { environment } from '../../../environments/environment';
import { ICampaign } from '../models/campaigns.model';
import { IListApiModel } from '../models/api.model';
import { SessionService } from './session.service';

export interface IContactMoveResponse {
  success: boolean;
  moved: {
    contact_id: number;
    message: string;
  }[] | number;
  failed: {
    contact_id: number;
    message: string;
  }[] | number;
  skipped_same_domain?: boolean;
  skipped_same_domain_count?: number;
  skipped_same_domain_contacts?: {
    contact_id: number;
    email: string;
    domain: string;
  }[];
  skipped_duplicated_count?: number;
  skipped_duplicated_contacts?: number[];
}

type SameDomainOptions = { allow_same_domain?: boolean; skip_same_domain?: boolean; skip_same_domain_check?: boolean };
type CreateContactOptions = SameDomainOptions & { validate_only?: boolean };

@Injectable({
  providedIn: 'root',
})
export class ContactsService {
  private normalizeSameDomainOptions(options?: SameDomainOptions): { skip_same_domain_check?: boolean } {
    if (!options) {
      return {};
    }

    const skipSameDomainCheck =
      options.skip_same_domain_check === true ||
      options.skip_same_domain === true ||
      options.allow_same_domain === true;

    return skipSameDomainCheck ? { skip_same_domain_check: true } : {};
  }

  makeContactPrimary(contact_id: number) {
    return this.http.put(
      environment.apiUrl + `/v1/contacts/${contact_id}/primary`,
      {},
    );
  }
  constructor(
    private http: HttpClient,
    private sessionService: SessionService,
  ) {
  }

  getContactsTotalActive(): Observable<ITotalActive> {
    return this.http.get<ITotalActive>(
      environment.apiUrl + '/v1/contacts/count',
    );
  }

  getLastContactsNumbers(): Observable<ILastContacts> {
    return this.http.get<ILastContacts>(
      environment.apiUrl + '/v1/contacts/last',
    );
  }

  getContactGroups(accountId: number, extraParams: { [key: string]: any } = null): Observable<IContactGroup[]> {
    let params = {
      paginated: 'false',
      account_id: accountId.toString(),
    };

    if (extraParams && Object.keys(extraParams).length > 0) {
      params = {
        ...params,
        ...extraParams,
      };
    }

    return this.http
      .get<IBaseTableResult<IContactGroup>>(
        environment.apiUrl + '/v1/contacts/groups',
        {
          params,
        },
      )
      .pipe(map((result: IBaseTableResult<IContactGroup>) => result.items));
  }

  viewContact(
    contactId: number,
    getExtendedInfo: boolean = false,
  ): Observable<IContact> {
    return this.http.get<IContact>(
      environment.apiUrl + `/v1/contacts/${contactId}`,
      {
        params: {
          campaign_info: getExtendedInfo.toString(),
        },
      },
    );
  }

  checkExistingGroup(groupName: string): Observable<IContactGroup[]> {
    return this.http
      .get<IBaseTableResult<IContactGroup>>(
        environment.apiUrl + '/v1/contacts/groups',
        {
          params: {
            group_name: groupName,
          },
        },
      )
      .pipe(map((result: IBaseTableResult<IContactGroup>) => result.items));
  }

  checkContactEmail(email: string): Observable<{ exists: boolean }> {
    return this.http.post<{ exists: boolean }>(
      environment.apiUrl + '/v1/contacts/checkemail',
      {
        email,
      },
    );
  }

  createContact(
    contact: IContact,
    options?: CreateContactOptions,
  ): Observable<{ contact_id: number; skipped?: boolean; valid?: boolean }> {
    const { validate_only, ...sameDomainOptions } = options || {};
    return this.http.post<{ contact_id: number; valid?: boolean }>(
      environment.apiUrl + '/v1/contacts',
      {
        ...contact,
        ...this.normalizeSameDomainOptions(sameDomainOptions),
        ...(validate_only ? { validate_only: true } : {}),
      },
    );
  }

  updateContact(
    contactId: number,
    contact: IContact | { group_id: number },
    options?: SameDomainOptions,
  ): Observable<{ contact_id: number; skipped?: boolean }> {
    return this.http.put<{ contact_id: number }>(
      environment.apiUrl + `/v1/contacts/${contactId}`,
      {
        ...contact,
        ...this.normalizeSameDomainOptions(options),
      },
    );
  }

  createGroup(groupName: string): Observable<{ contact_group_id: number }> {
    return this.http.post<{ contact_group_id: number }>(
      environment.apiUrl + '/v1/contacts/groups',
      {
        group_name: groupName,
      },
    ).pipe(tap(() => this.sessionService.refreshInboxStatus()));
  }

  getContactActivityNote(
    contactId: number,
    activityId: number,
  ): Observable<IContactActivityNote> {
    return this.http.get<IContactActivityNote>(
      environment.apiUrl + `/v1/contacts/${contactId}/activity/${activityId}`,
    );
  }

  getUploadFileFieldsMap(fileId: number): Observable<IContactFieldsMapping> {
    return this.http.get<IContactFieldsMapping>(
      environment.apiUrl + `/v1/contacts/filemap/${fileId}`,
    );
  }

  importUserContacts(
    mappings: { [key: string]: string },
    fileId: number,
    groupId: number,
    keepGroup = true,
  ): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      environment.apiUrl + '/v1/contacts/import',
      {
        map: mappings,
        file_id: fileId,
        group_id: groupId,
        keep_group: keepGroup,
      },
    );
  }

  quickResponse(data, type): Observable<{ contact_id: number }> {
    if (type === 'send-email') {
      data.only_send = true;
    }

    if (type === 'conversation' || type === 'send-email') {
      if (data.name === 'Choose File') {
        delete data.name;
      }
      return this.http.post<{ contact_id: number }>(
        environment.apiUrl + '/v1/messages/send-email',
        data,
      );
    } else {
      for (const key in data) {
        if (
          data.hasOwnProperty(key) &&
          typeof data[key] === 'string' &&
          data[key].length === 0
        ) {
          delete data[key];
        }
      }
      return this.http.post<{ contact_id: number }>(
        environment.apiUrl + '/v1/messages/' + type,
        data,
      );
    }
  }

  getReplies(): Observable<any> {
    return this.http.get<IContactActivityNote>(
      environment.apiUrl + `/v1/users/responses`,
    );
  }

  getTemplateReplies(type: string, contactId: number, accountId?: number): Observable<any> {
    if (type !== 'order') {
      const params: { contact_id: number, account_id?: number } = {
        contact_id: contactId,
      };

      if (accountId) {
        params.account_id = accountId;
      }
      const httpParams: HttpParams = new HttpParams({
        fromObject: params,
      });

      return this.http.get<IContactActivityNote>(
        environment.apiUrl + `/v1/replies/${type}`,
        {
          params: httpParams,
        },
      );
    } else {
      return of({});
    }
  }

  getProducts(): Observable<any> {
    return this.http.get<IContactActivityNote>(
      environment.apiUrl + `/v1/products`,
    );
  }

  getExport(type: string, accountId?: number): Observable<any> {
    let path = `/v1/messages/export/${type}`;

    if (accountId) {
      path += `?account_id=${accountId}`;
    }
    return this.http.get(environment.apiUrl + path, {
      responseType: 'blob' as 'json',
    });
  }

  getDownload(id): Observable<any> {
    return this.http.get(environment.apiUrl + `/v1/files/${id}`, {
      responseType: 'blob' as 'json',
      observe: 'response',
    });
  }

  getExportMeeting(format, id): Observable<any> {
    return this.http.get(
      environment.apiUrl + `/v1/meetings/export/${id}?format=${format}`,
      { responseType: 'blob' as 'json' },
    );
  }

  getActiveCampaigns(): Observable<any> {
    return this.http.get(environment.apiUrl + '/v1/campaigns?active=true');
  }

  getPublishedCampaigns(): Observable<{ items: ICampaign[] }> {
    return this.http.get<{ items: ICampaign[] }>(
      environment.apiUrl + '/v1/campaigns?published=true',
    );
  }

  updateGroup(group, item): Observable<{ success: boolean }> {
    return this.http.put<{ success: boolean }>(
      environment.apiUrl + `/v1/contacts/groups/${group.group_id}`,
      {
        campaign_id: item,
      },
    ).pipe(tap(() => this.sessionService.refreshInboxStatus()));
  }

  getContactGroupsByCampaignId(
    campaignId: number,
  ): Observable<{ items: IContactGroup[] }> {
    return this.http.get<{ items: IContactGroup[] }>(
      environment.apiUrl + `/v1/contacts/groups?campaign_id=${campaignId}`,
    );
  }

  publishCampaign(item: number): Observable<{ success: boolean }> {
    return this.http.put<{ success: boolean }>(environment.apiUrl + `/v1/campaigns/${item}`, {})
      .pipe(tap(() => this.sessionService.refreshInboxStatus()));
  }

  getDownloadContactActivityUrl(contactId: number, token: string): string {
    return (
      environment.apiUrl +
      `/v1/contacts/${contactId}/history?generate_report=true&access_token=${token}`
    );
  }

  forwardResponse(messageId: number, includeContactDetails: boolean = false): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      environment.apiUrl + `/v1/messages/${messageId}/forward`,
      {
        contact_details: includeContactDetails,
      }
    );
  }

  forwardResponseToRecipients(
    messageId: number,
    recipients: string,
    includeContactDetails: boolean = false,
    notes: string = '',
  ): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      environment.apiUrl + `/v1/messages/${messageId}/forward`,
      {
        forward_email: recipients,
        contact_details: includeContactDetails,
        notes: notes || '',
      },
    );
  }

  sendCustomEmail1(data: {
    to: string;
    subject: string;
    message: string;
    attachments: any[];
  }): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      environment.apiUrl + '/v1/messages/custom-email',
      data,
    );
  }

  setContactVipStatus(contactId: number, value: boolean) {
    return this.http.put(environment.apiUrl + '/v1/contacts/' + contactId, {
      vip: value,
    });
  }

  getVipMessageReply(): Observable<{ reply_content: string }> {
    return this.http.get<{ reply_content: string }>(
      environment.apiUrl + '/v1/replies/vip-message',
    );
  }

  leftVipMessage(
    contactId: number,
    subject: string,
    message: string,
  ): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      environment.apiUrl + '/v1/messages/onetime',
      {
        contacts: contactId.toString(),
        vip: true,
        subject,
        message,
      },
    );
  }

  moveContactsToAccount(
    contactGroupId: number,
    accountId: number,
    options?: SameDomainOptions,
  ): Observable<IContactMoveResponse> {
    return this.http.put<IContactMoveResponse>(environment.apiUrl + `/v1/contacts/move`, {
      group_id: contactGroupId,
      account_id: accountId,
      ...this.normalizeSameDomainOptions(options),
    });
  }

  moveContactToAccount(
    contactId: number,
    accountId: number,
    options?: SameDomainOptions,
  ): Observable<any> {
    return this.http.put(environment.apiUrl + `/v1/contacts/move_one`, {
      contact_id: contactId,
      account_id: accountId,
      ...this.normalizeSameDomainOptions(options),
    });
  }

  moveContactsToAccounts(
    contactIds: number[],
    accountIds: number[],
    groupIds: number[],
    options?: SameDomainOptions,
  ): Observable<IMoveContactResult> {
    return this.http.put<IMoveContactResult>(environment.apiUrl + '/v1/contacts/move', {
      contact_id: contactIds,
      account_id: accountIds,
      group_to: groupIds,
      ...this.normalizeSameDomainOptions(options),
    });
  }

  sendActivityNote(
    contactId: number,
    note: string,
    noteType: 'phone' | 'note',
    title: string,
    conversationId?: number,
  ): Observable<{ activity_id: number }> {
    return this.http.post<{ activity_id: number }>(
      environment.apiUrl + `/v1/contacts/${contactId}/activity`,
      {
        note,
        note_type: noteType,
        title,
        conversation_id: conversationId,
      },
    );
  }

  turnBackContactToCampaign(
    contactId: number,
  ): Observable<{ success: boolean }> {
    return this.http.get<{ success: boolean }>(
      environment.apiUrl + `/v1/contacts/${contactId}/campaign`,
    );
  }

  sendGroupToCampaign(
    group: IUserContactGroup,
    data: { campaign_id: number; start_date: string },
  ): Observable<{ contact_group_id: number }> {
    return this.http.put<{ contact_group_id: number }>(
      environment.apiUrl + `/v1/contacts/groups/${group.group_id}`,
      {
        group_name: group.group_name,
        ...data,
      },
    );
  }

  renameContactGroup(
    groupId: number,
    newGroupName: string,
  ): Observable<{ contact_group_id: number }> {
    return this.http.put<{ contact_group_id: number }>(
      environment.apiUrl + `/v1/contacts/groups/${groupId}`,
      {
        group_name: newGroupName,
      },
    );
  }

  mergeContacts(
    sourceContactId: number,
    destinationContactId: number,
    keep: boolean = false,
  ): Observable<{ success: boolean, message_id: number }> {
    return this.http.post<{ success: boolean, message_id: number }>(
      environment.apiUrl + '/v1/contacts/merge',
      {
        source_id: sourceContactId,
        destination_id: destinationContactId,
        keep,
      },
    );
  }

  setMessageToRemove(contactId: number, content: string): Observable<any> {
    return this.http.post(environment.apiUrl + '/v1/messages/remove', {
      contact_id: contactId,
      message: content,
      update: true,
    });
  }

  setMessageToOkGotIt(contactId: number): Observable<any> {
    return this.http.post(environment.apiUrl + '/v1/messages/okgotit', {
      contact_id: contactId,
    });
  }

  resetPosition(groupId: number, withReplies = false): Observable<any> {
    return this.http.put(
      environment.apiUrl + `/v1/contacts/groups/${groupId}/reset`,
      {
        with_replies: withReplies,
      },
    );
  }

  mergeGroups(
    sourceGroupId: number,
    targetGroupId: number,
  ): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      environment.apiUrl + '/v1/contacts/groups/merge',
      {
        source_id: sourceGroupId,
        destination_id: targetGroupId,
      },
    );
  }

  getDownloadGroupContacts(
    accountId: number,
    groupId: number,
    format: 'csv' | 'xls',
  ): Observable<any> {
    return this.http.get(
      environment.apiUrl + `/v1/contacts/${accountId}/export`,
      {
        responseType: 'blob' as 'json',
        params: new HttpParams({
          fromObject: {
            group_id: groupId.toString(),
            format,
          },
        }),
      },
    );
  }

  deleteContact(contactId: number): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(
      environment.apiUrl + `/v1/contacts/${contactId}`,
    );
  }

  moveMultipleContacts(data: {
    contact_id: number[];
    account_id: number[];
    group_id: number[];
  }, options?: SameDomainOptions): Observable<IContactMoveResponse> {
    return this.http.put<IContactMoveResponse>(
      environment.apiUrl + `/v1/contacts/move`,
      {
        contact_id: data.contact_id.join(','),
        account_id: data.account_id.join(','),
        group_to: data.group_id.length ? data.group_id.join(',') : null,
        ...this.normalizeSameDomainOptions(options),
      },
    );
  }

  downloadMoveContactsTemplate(templateName: string) {
    return this.http.get(environment.apiUrl + `/v1/files/template/${templateName}`, {
      responseType: 'blob' as 'json',
      observe: 'response',
    });
  }

  getContacts(search: string, accountId?: number): Observable<{ count: number }> {
    const params: any = {
      search,
    };

    if (accountId) {
      params.account_id = accountId;
    }

    return this.http.get<IListApiModel<IContact>>(environment.apiUrl + `/v1/contacts`, {
      params,
    }).pipe(
      map(response => {
        return {
          count: response.total_count,
        };
      }),
    );
  }

  sendContactToCampaign(contactId: number, campaignId: number, groupId: number, startDate: string): Observable<any> {
    return this.http.put(environment.apiUrl + `/v1/contacts/${contactId}/campaign`, {
      group_id: groupId,
      campaign_id: campaignId,
      start_date: startDate,
    });
  }

  deleteUploadedContactsFile(uploadId: number): Observable<any> {
    return this.http.delete(environment.apiUrl + `/v1/files/${uploadId}`);
  }

  deleteGroup(
    groupId: number,
    options?: { purgeMode?: boolean },
  ): Observable<{
    success: boolean;
    deleted_count?: number;
    group_deleted?: boolean;
    group_id?: number;
  }> {
    let params = new HttpParams();
    if (options?.purgeMode) {
      params = params.set('purge_mode', 'true');
    }
    return this.http
      .delete<{
        success: boolean;
        deleted_count?: number;
        group_deleted?: boolean;
        group_id?: number;
      }>(environment.apiUrl + `/v1/contacts/groups/${groupId}`, { params })
      .pipe(tap(() => this.sessionService.refreshInboxStatus()));
  }

  getContactsByGroupAndStatus(groupIds: number[], statuses: string[], tags: string[]): Observable<IListApiModel<IContactItem>> {
    const params: any = {
      group_id: groupIds.join(','),
      contact_status: statuses.join(','),
      paginated: 'false',
      tags: tags.join(','),
    };

    return this.http.get<IListApiModel<IContactItem>>(environment.apiUrl + `/v1/contacts`, {
      params,
    });
  }
}
